import { lazy } from 'react';
import type { ShopTypeEnum } from 'components/AuthShop/components/ShopeeCNSCGuide';
import { definePanel, resolvePanelAction, PanelDefinition } from './panel-actions';
import { parseStoreAuthParams } from './store-auth';
import { parseAccountCreateParams } from './account-create';
import { parseQuickReplyCreate } from './quick-reply-create';
import { knowledgeFormNavigationSearch, knowledgeFormParser } from './knowledge-form';
import { parseAutoMessageCreate } from './auto-message-create';
import { AGENT_PAGES } from './navigation';
import { configurationParser } from './configuration-create';

const QuickReplyCreatePanel = lazy(() => import('./quick-reply-create-panel'));
const KnowledgeFormPanel = lazy(() => import('./knowledge-form-panel'));
const AutoMessageCreatePanel = lazy(() => import('./auto-message-create-panel'));
const ExistingCreatePanel = lazy(() => import('./existing-create-panel'));
const ConfigurationCreatePanel = lazy(() => import('./configuration-create-panel'));
const StoreAuthForm = lazy(() => import('components/AuthShop/NewbieGuideDialog'));
const AccountCreatePanel = lazy(() => import('./account-create-panel'));

/** Register a business panel here; location choice and permission checks are host-owned. */
export const AGENT_PANELS: Record<string, PanelDefinition> = {
  knowledge_quick_reply_create: definePanel({
    workspaceId: 'knowledge-scripts-quick-replies',
    parseParams: parseQuickReplyCreate,
    render: (params, props) => <QuickReplyCreatePanel params={params} {...props} />,
  }),
  knowledge_store_create: definePanel({
    workspaceId: 'knowledge-store',
    parseParams: knowledgeFormParser(false),
    navigationSearch: knowledgeFormNavigationSearch,
    entrySelector: 'button[data-agent-panel="knowledge_store_create"]',
    render: (params, props) => <KnowledgeFormPanel params={params} {...props} />,
  }),
  knowledge_store_edit: definePanel({
    workspaceId: 'knowledge-store',
    parseParams: knowledgeFormParser(true),
    navigationSearch: knowledgeFormNavigationSearch,
    render: (params, props) => <KnowledgeFormPanel edit params={params} {...props} />,
  }),
  reception_invite_follower_create: definePanel({
    workspaceId: 'reception-automatic-messages-follow-request',
    parseParams: parseAutoMessageCreate,
    render: (params, props) => (
      <AutoMessageCreatePanel scenarioType="INVITE_FOLLOWER" params={params} {...props} />
    ),
  }),
  reception_welcome_reply_create: definePanel({
    workspaceId: 'reception-automatic-messages-welcome',
    parseParams: parseAutoMessageCreate,
    render: (params, props) => (
      <AutoMessageCreatePanel scenarioType="WELCOME_REPLY" params={params} {...props} />
    ),
  }),
  reception_timeout_reply_create: definePanel({
    workspaceId: 'reception-automatic-messages-timeout-reply',
    parseParams: parseAutoMessageCreate,
    render: (params, props) => (
      <AutoMessageCreatePanel scenarioType="TIMEOUT_REPLY" params={params} {...props} />
    ),
  }),
  knowledge_quick_reply_group_create: definePanel({
    workspaceId: 'knowledge-scripts-quick-replies',
    parseParams: configurationParser(['name', 'channel', 'group_type']),
    render: (params, props) => (
      <ExistingCreatePanel target="knowledge_quick_reply_group_create" params={params} {...props} />
    ),
  }),
  reception_important_reminder_create: definePanel({
    workspaceId: 'reception-tools-important-reminders',
    parseParams: configurationParser(['name', 'enable']),
    render: (params, props) => (
      <ExistingCreatePanel
        target="reception_important_reminder_create"
        params={params}
        {...props}
      />
    ),
  }),
  organization_role_create: definePanel({
    workspaceId: 'management-employees-departments',
    parseParams: configurationParser(['name', 'description', 'department_id']),
    render: (params, props) => (
      <ExistingCreatePanel target="organization_role_create" params={params} {...props} />
    ),
  }),
  organization_department_create: definePanel({
    workspaceId: 'management-employees-departments',
    parseParams: configurationParser(['name']),
    render: (params, props) => (
      <ConfigurationCreatePanel
        target="organization_department_create"
        params={params}
        {...props}
      />
    ),
  }),
  reception_sensitive_word_create: definePanel({
    workspaceId: 'reception-tools-sensitive-words',
    parseParams: configurationParser(['word', 'platforms', 'regions']),
    render: (params, props) => (
      <ConfigurationCreatePanel
        target="reception_sensitive_word_create"
        params={params}
        {...props}
      />
    ),
  }),
  organization_account_create: definePanel({
    workspaceId: 'management-employees-accounts',
    parseParams: parseAccountCreateParams,
    render: (params, props) => <AccountCreatePanel initialValues={params} {...props} />,
  }),
  store_auth: definePanel({
    workspaceId: 'management-stores',
    parseParams: parseStoreAuthParams,
    render: (params, { presentation, onClose }) => (
      <StoreAuthForm
        visible
        closable
        presentation={presentation}
        defaultPlatform={params.platform}
        initialStoreType={params.storeType}
        initialShopeeShopType={params.shopeeShopType as ShopTypeEnum | undefined}
        initialRegion={params.region}
        initialDepartmentIds={params.departmentIds}
        onCloseSidebar={onClose}
        onCancel={onClose}
      />
    ),
  }),
};
export const parsePanelAction = (raw: unknown) => resolvePanelAction(raw, AGENT_PANELS);

/** Distinguish another client's destinations from malformed actions. Never log form data. */
export function isUnsupportedDestination(raw: unknown): boolean {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (value?.version !== 1 || !Array.isArray(value.widgets) || value.widgets.length !== 1)
      return false;
    const widget = value.widgets[0];
    if (widget?.type === 'workspace_panel' && typeof widget.panel === 'string') {
      return !Object.prototype.hasOwnProperty.call(AGENT_PANELS, widget.panel);
    }
    return (
      widget?.type === 'workspace_navigation' &&
      typeof widget.workspace_id === 'string' &&
      !Object.prototype.hasOwnProperty.call(AGENT_PAGES, widget.workspace_id)
    );
  } catch {
    return false;
  }
}
